import React, { useState, useEffect, useRef } from 'react';
import { RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button, Text } from 'components/Elements';
import { VerificationCodeInputField } from 'components/Form';
import { googleAuthSetup } from '../api/googleAuthSetup';
import { verifyGoogleAuthSetup } from '../api/googleAuthSetup_verify';
import Cookies from 'js-cookie';

export const GoogleAuthSetupForm = () => {
  const [qrCode, setQrCode] = useState(null);
  const [verificationCode, setVerificationCode] = useState(
    new Array(6).fill('')
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef(new Array(6).fill(null));
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQrCode = async () => {
      try {
        const response = await googleAuthSetup();
        setQrCode(response.qrCode);
      } catch (err) {
        setError('Failed to load QR code. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchQrCode();
  }, []);

  const handleVerify = async () => {
    setVerifying(true);
    setError('');
    try {
      await verifyGoogleAuthSetup({ authenticatorCode: verificationCode });
      setSuccess(true);
      Cookies.set('authentication_level_google_auth', 'pass');
      navigate('/auth/security/setup-required');
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (index, value) => {
    if (!isNaN(value) && value.length <= 1) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  return (
    <div className="">
      {loading ? (
        <p className="text-center text-gray-100 flex items-center justify-center  mx-auto w-full h-full mb-4 animate-pulse rounded-lg">
          Generating QRCode...
        </p>
      ) : error ? (
        <>
          <p className="text-red-500 text-center mt-3">{error}</p>
          <button className="flex flex-row items-center space-x-2">
            <RefreshCcw size={12} />
            <Text>Try again</Text>
          </button>
        </>
      ) : (
        <div className="text-start">
          <img
            src={qrCode}
            alt="Google Authenticator QR Code"
            className=" w-full h-full mb-4 border-2 border-gray-300 rounded-lg"
          />
          <p className="text-sm text-gray-100">
            Scan the QR code with your Google Authenticator app.
          </p>
        </div>
      )}

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-100 mb-2">
          Enter 6-digit Code
        </label>
        <div className="flex space-x-2 justify-center">
          <VerificationCodeInputField
            length={6}
            onChange={(code) => setVerificationCode(code)}
          />
        </div>
      </div>

      {success && (
        <p className="text-green-500 text-center mt-3">
          Verification successful!
        </p>
      )}

      <Button
        onClick={handleVerify}
        disabled={verifying || verificationCode?.length !== 6}
        variant="glass"
        className="mt-4 w-full"
      >
        {verifying ? 'Verifying...' : 'Verify'}
      </Button>
    </div>
  );
};
